import { useState } from "react";
import dayjs from "dayjs";
import {
  useGetAccountingNotes,
  useGetReturnAccountingVoucher
} from "@app/services/accountingNotes/hooks";
import { GCidAccount } from "@app/config/globalsConst";
import { warningNotification } from "@app/common/components/notifications";
import routes from "@app/config/routes";
import { downloadExcel } from "@app/utils/downloadExcel";
import { useGenericData } from "@app/store/useGenericData";

import ExcelIcon from "@app/assets/excel.svg";

import { ActionIcon, Grid, Image, Menu, Select, Table, TextInput, Tooltip } from "@mantine/core";
import { IconDotsVertical, IconEye } from "@tabler/icons-react";
import { Template } from "@app/common/components/Template";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import AppPagination from "@app/common/components/AppPagination";
import { useNavigate } from "react-router-dom";

import type {
  IAccountingNotesFilters,
  IAccountingNoteItem
} from "@app/services/accountingNotes/types";

const NOTE_TYPE_OPTIONS = [
  { value: "credit", label: "Crédito" },
  { value: "debit", label: "Débito" }
];

const defaultFilters: Omit<IAccountingNotesFilters, "eaccount"> = {
  startDate: dayjs().format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  table: "dis",
  noteType: "",
  search: "",
  page: 1,
  perpage: 10
};

const formatCurrency = (value: number) => {
  return `$ ${value.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatNoteType = (noteType: string) => {
  return noteType === "credit" ? "Crédito" : "Débito";
};

const formatCreatedBy = (createdBy: IAccountingNoteItem["createdBy"]) => {
  const parts = [createdBy.firstName, createdBy.secondName, createdBy.lastName].filter(Boolean);
  return parts.join(" ");
};

const AccountingNotes = () => {
  const navigate = useNavigate();
  const eaccount = GCidAccount();
  const siteId = useGenericData((state) => state.genericData.siteId);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [hasSubmittedFilters, setHasSubmittedFilters] = useState(true);

  const { data: notesData } = useGetAccountingNotes(
    { ...appliedFilters, eaccount },
    hasSubmittedFilters
  );

  const { refetch: refetchVoucher } = useGetReturnAccountingVoucher({
    eaccount,
    siteId: siteId || 0,
    dateMin: appliedFilters.startDate,
    dateMax: appliedFilters.endDate
  });

  const handleDownloadExcel = async () => {
    if (!siteId) {
      warningNotification("No se ha seleccionado una sede");
      return;
    }
    if (!filters.startDate || !filters.endDate) {
      warningNotification("Los campos 'Fecha desde' y 'Fecha hasta' son obligatorios");
      return;
    }
    const result = await refetchVoucher();
    if (result.data?.results?.base64) {
      downloadExcel(result.data.results.base64, "comprobante-devoluciones.xlsx");
    }
  };

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!filters.startDate || !filters.endDate) {
      warningNotification("Los campos 'Fecha desde' y 'Fecha hasta' son obligatorios");
      return;
    }

    const nextFilters = { ...filters, page: 1 };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setHasSubmittedFilters(true);
  };

  const handleChangePage = (value: number) => {
    setFilters((state) => ({ ...state, page: value }));

    if (hasSubmittedFilters) {
      setAppliedFilters((state) => ({ ...state, page: value }));
    }
  };

  const filtersHeader = () => {
    return (
      <Grid columns={12} align="flex-end" mb="lg" w="100%">
        <Grid.Col span={2}>
          <TextInput
            data-testid="accountingnotes-start-date-input"
            label="Fecha desde"
            type="date"
            max={filters.endDate || undefined}
            placeholder="Escribir..."
            value={filters.startDate}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, startDate: target.value }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            data-testid="accountingnotes-end-date-input"
            label="Fecha hasta"
            type="date"
            min={filters.startDate || undefined}
            placeholder="Escribir..."
            value={filters.endDate}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, endDate: target.value }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="accountingnotes-note-type-select"
            label="Tipo de nota"
            data={NOTE_TYPE_OPTIONS}
            clearable
            placeholder="Seleccionar..."
            value={filters.noteType || null}
            onChange={(value) => {
              setFilters((state) => ({ ...state, noteType: value || "", page: 1 }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            data-testid="accountingnotes-search-input"
            placeholder="Buscar nota..."
            value={filters.search}
            onChange={({ target }) => {
              setFilters((state) => ({ ...state, search: target.value }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2} className="flex gap-sm" style={{ alignItems: "flex-end", gap: 8 }}>
          <FilterSearchButton />
          <Tooltip label="Comprobante de devoluciones" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={handleDownloadExcel}
              data-testid="accountingnotes-download-voucher-button"
            >
              <Image src={ExcelIcon} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (item: IAccountingNoteItem, index: number) => {
    const creditNoteDetailPath = routes.getAccountingNotesCreditDetail.replace(
      ":invNoteId",
      String(item.id)
    );

    return (
      <Table.Tr key={item.id} data-testid={`accountingnotes-row-${index}`}>
        <Table.Td>{item.invoiceId}</Table.Td>
        <Table.Td ta="center">{item.createdAt}</Table.Td>
        <Table.Td ta="center">{formatNoteType(item.noteType)}</Table.Td>
        <Table.Td ta="right">{formatCurrency(item.originalValue)}</Table.Td>
        <Table.Td ta="right">{formatCurrency(item.newValue)}</Table.Td>
        <Table.Td>{formatCreatedBy(item.createdBy)}</Table.Td>
        <Table.Td ta="center">
          <Menu position="bottom-end" withArrow data-testid={`accountingnotes-menu-${index}`}>
            <Menu.Target>
              <ActionIcon variant="subtle" data-testid={`accountingnotes-menu-button-${index}`}>
                <IconDotsVertical />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye />}
                data-testid={`accountingnotes-view-credit-note-item-${index}`}
                disabled={item.noteType !== "credit"}
                onClick={() => {
                  navigate(creditNoteDetailPath);
                }}
              >
                Ver nota crédito
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="accountingnotes-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>No. de factura</Table.Th>
            <Table.Th ta="center">Fecha</Table.Th>
            <Table.Th ta="center">Tipo</Table.Th>
            <Table.Th ta="right">Valor factura</Table.Th>
            <Table.Th ta="right">Nuevo valor factura</Table.Th>
            <Table.Th>Creado por</Table.Th>
            <Table.Th ta="center">Opciones</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {notesData?.results?.length ? (
            notesData.results.map((item, index) => renderRow(item, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={7} data-testid="accountingnotes-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Historial de notas">
      <form onSubmit={handleSubmitFilters} data-testid="accountingnotes-filters">
        {filtersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        total={notesData?.rowTotal || 0}
        withDetails
        value={filters.page || 1}
        onChange={handleChangePage}
        data-testid="accountingnotes-pagination"
      />
    </Template>
  );
};

export default AccountingNotes;
